import { useParams } from "@tanstack/react-router";
import {
  Bell,
  Settings as Cog,
  Eye,
  KeyRound,
  Keyboard,
  Shield,
  Sparkles,
  User,
  Wrench,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useUI } from "../../stores/ui";

const TABS = [
  { id: "profile", label: "Perfil", icon: User },
  { id: "appearance", label: "Apariencia", icon: Eye },
  { id: "editor", label: "Editor", icon: Sparkles },
  { id: "notifications", label: "Notificaciones", icon: Bell },
  { id: "api", label: "API tokens", icon: KeyRound },
  { id: "shortcuts", label: "Atajos", icon: Keyboard },
  { id: "account", label: "Cuenta", icon: Shield },
  { id: "advanced", label: "Avanzado", icon: Wrench },
];

export function SettingsPage() {
  const params = useParams({ strict: false }) as { section?: string };
  const [tab, setTab] = useState(params.section ?? "profile");

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <header className="mb-4 flex items-center gap-2">
        <Cog size={20} />
        <h1 className="text-2xl font-semibold">Ajustes</h1>
      </header>

      <div className="flex gap-6">
        <nav className="w-48 shrink-0">
          <ul className="space-y-0.5">
            {TABS.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm ${
                    tab === t.id
                      ? "bg-(--bg-active) font-medium"
                      : "hover:bg-(--bg-hover) text-(--text-secondary)"
                  }`}
                >
                  <t.icon size={14} /> {t.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="flex-1">
          {tab === "profile" && <ProfileTab />}
          {tab === "appearance" && <AppearanceTab />}
          {tab === "editor" && <EditorTab />}
          {tab === "notifications" && <NotificationsTab />}
          {tab === "api" && <ApiTokensTab />}
          {tab === "shortcuts" && <ShortcutsTab />}
          {tab === "account" && <AccountTab />}
          {tab === "advanced" && <AdvancedTab />}
        </main>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6 rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4">
      <h2 className="mb-3 text-sm font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function ProfileTab() {
  return (
    <Section title="Perfil">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Field label="Display name">
          <Input defaultValue="Manuel Miguez" />
        </Field>
        <Field label="Email">
          <Input defaultValue="ai@iort.io" disabled />
        </Field>
        <Field label="Organización">
          <Input defaultValue="iort.io" disabled />
        </Field>
        <Field label="Rol">
          <Input defaultValue="admin" disabled />
        </Field>
      </div>
      <p className="mt-3 text-xs text-(--text-tertiary)">
        Email y org provienen de Cloudflare Access (no editables aquí).
      </p>
    </Section>
  );
}

function AppearanceTab() {
  const ui = useUI();
  return (
    <>
      <Section title="Tema">
        <div className="flex gap-2" data-testid="theme-picker">
          {(["light", "dark"] as const).map((t) => (
            <button
              type="button"
              key={t}
              onClick={() => ui.setTheme(t)}
              data-testid={`theme-${t}`}
              className={`flex-1 rounded-md border px-3 py-2 text-sm capitalize ${
                ui.theme === t
                  ? "border-(--accent-500) bg-(--accent-50)"
                  : "border-(--border-subtle)"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-(--text-tertiary)">
          La paleta es terracota fija (decisión de diseño 2026-05-25). Sólo se ofrece toggle claro /
          oscuro.
        </p>
      </Section>
      <Section title="Fuente de lectura">
        <div className="flex gap-2" data-testid="prose-font-picker">
          {(["serif", "sans"] as const).map((f) => (
            <button
              type="button"
              key={f}
              onClick={() => ui.setProseFont(f)}
              data-testid={`prose-font-${f}`}
              className={`flex-1 rounded-md border px-3 py-2 text-sm capitalize ${
                ui.proseFont === f
                  ? "border-(--accent-500) bg-(--accent-50)"
                  : "border-(--border-subtle)"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </Section>
      <Section title="Margen del documento (chips de anotación)">
        <button
          type="button"
          onClick={() => ui.toggleMarginChips()}
          data-testid="margin-chips-toggle-settings"
          className={`rounded-md border px-3 py-1.5 text-sm ${
            ui.marginChips ? "border-(--accent-500) bg-(--accent-50)" : "border-(--border-subtle)"
          }`}
        >
          {ui.marginChips ? "Mostrando chips" : "Chips ocultos"}
        </button>
        <p className="mt-2 text-xs text-(--text-tertiary)">
          También se puede alternar desde el header del documento.
        </p>
      </Section>
      <Section title="Densidad">
        <div className="flex gap-2">
          {(["comfortable", "compact"] as const).map((d) => (
            <button
              type="button"
              key={d}
              onClick={() => ui.setDensity(d)}
              className={`rounded-md border px-3 py-1.5 text-sm ${
                ui.density === d
                  ? "border-(--accent-500) bg-(--accent-50)"
                  : "border-(--border-subtle)"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </Section>
      <Section title="Idioma de la UI">
        <div className="flex gap-2">
          {(["es", "en"] as const).map((l) => (
            <button
              type="button"
              key={l}
              onClick={() => ui.setLocale(l)}
              className={`rounded-md border px-3 py-1.5 text-sm uppercase ${
                ui.locale === l
                  ? "border-(--accent-500) bg-(--accent-50)"
                  : "border-(--border-subtle)"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </Section>
    </>
  );
}

function EditorTab() {
  return (
    <Section title="Comportamiento del editor">
      <ul className="space-y-2 text-sm">
        <Toggle label="Autosave de borradores cada 5 s" defaultOn />
        <Toggle label="Mostrar plegado de secciones por defecto" defaultOn />
        <Toggle label="Mostrar línea base 8 px en hover" defaultOn={false} />
        <Toggle label="Avisar al cambiar de doc sin guardar" defaultOn />
      </ul>
    </Section>
  );
}

function NotificationsTab() {
  return (
    <Section title="Notificaciones">
      <ul className="space-y-2 text-sm">
        <Toggle label="Email cuando termina mi compose" defaultOn />
        <Toggle label="Email cuando un job de ingesta entra en mi inbox" defaultOn />
        <Toggle label="Notificación in-app de @menciones" defaultOn />
        <Toggle label="Resumen semanal por email" defaultOn={false} />
      </ul>
    </Section>
  );
}

function ApiTokensTab() {
  const tokens = [
    { name: "claude-desktop", scopes: ["docs:read", "notebooks:write"], lastUsed: "2026-05-15" },
    { name: "cursor-mcp", scopes: ["docs:read"], lastUsed: "2026-05-14" },
  ];
  return (
    <Section title="API tokens (MCP)">
      <ul className="divide-y divide-(--border-subtle) text-sm">
        {tokens.map((t) => (
          <li key={t.name} className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium">{t.name}</p>
              <p className="text-xs text-(--text-tertiary)">
                {t.scopes.map((s) => (
                  <Badge key={s} variant="muted" className="mr-1">
                    {s}
                  </Badge>
                ))}
                Última uso {t.lastUsed}
              </p>
            </div>
            <Button variant="ghost" size="sm">
              Revocar
            </Button>
          </li>
        ))}
      </ul>
      <Button
        variant="primary"
        size="sm"
        className="mt-2"
        onClick={() => toast.success("Token creado")}
      >
        Crear token
      </Button>
    </Section>
  );
}

function ShortcutsTab() {
  const sh = [
    { keys: "⌘K", desc: "Command palette" },
    { keys: "⌘B", desc: "Toggle sidebar" },
    { keys: "⌘J", desc: "Toggle chat panel" },
    { keys: "⌘.", desc: "Toggle AI panel" },
    { keys: "G + D", desc: "Ir a docs" },
    { keys: "G + N", desc: "Ir a notebooks" },
    { keys: "G + C", desc: "Ir a chat" },
    { keys: "⌘↵", desc: "Enviar pregunta" },
    { keys: "Esc", desc: "Cancelar streaming / cerrar dialogos" },
  ];
  return (
    <Section title="Atajos de teclado">
      <ul className="divide-y divide-(--border-subtle) text-sm">
        {sh.map((s) => (
          <li key={s.keys} className="flex items-center justify-between py-1.5">
            <span>{s.desc}</span>
            <kbd className="rounded bg-(--bg-base) px-1.5 py-0.5 font-mono text-xs">{s.keys}</kbd>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function AccountTab() {
  return (
    <Section title="Cuenta">
      <ul className="space-y-3 text-sm">
        <li>
          <p className="font-medium">Sesión activa</p>
          <p className="text-xs text-(--text-tertiary)">
            Identificado mediante Cloudflare Access JWT (caduca cada 24 h).
          </p>
        </li>
        <li>
          <p className="font-medium">Exportar mis datos</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toast.message("Export bajo demanda — Phase 7")}
          >
            Solicitar exportación
          </Button>
        </li>
        <li>
          <p className="font-medium text-(--error)">Eliminar mi cuenta</p>
          <Button
            variant="ghost"
            size="sm"
            className="text-(--error)"
            onClick={() => toast.message("Contacta soporte")}
          >
            Eliminar
          </Button>
        </li>
      </ul>
    </Section>
  );
}

function AdvancedTab() {
  return (
    <Section title="Avanzado">
      <ul className="space-y-2 text-sm">
        <Toggle label="Modo debug (mostrar request_id en errores)" defaultOn={false} />
        <Toggle label="Telemetría opcional de uso" defaultOn />
        <Toggle label="Mostrar score_breakdown en resultados" defaultOn={false} />
      </ul>
      <p className="mt-3 text-xs text-(--text-tertiary)">
        Build: <code>{import.meta.env.MODE}</code> · Studio @ commit{" "}
        <code>{import.meta.env.VITE_GIT_SHA ?? "dev"}</code>
      </p>
    </Section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  // a11y: wrap with <label> so the input child is associated implicitly.
  return (
    <div className="flex flex-col gap-1">
      {/* biome-ignore lint/a11y/noLabelWithoutControl: children is always an Input/Select control rendered inside the label */}
      <label className="text-[10px] uppercase tracking-wider text-(--text-tertiary)">
        {label}
        {children}
      </label>
    </div>
  );
}

function Toggle({ label, defaultOn = true }: { label: string; defaultOn?: boolean }) {
  const [on, setOn] = useState(defaultOn);
  return (
    <li className="flex items-center justify-between">
      <span>{label}</span>
      <button
        type="button"
        onClick={() => setOn((v) => !v)}
        className={`h-5 w-9 rounded-full transition ${on ? "bg-(--accent-500)" : "bg-(--bg-base)"}`}
        aria-pressed={on}
      >
        <span
          className={`block h-4 w-4 rounded-full bg-white transition ${on ? "translate-x-4" : "translate-x-0.5"}`}
        />
      </button>
    </li>
  );
}
